import type { FC } from "react";
import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import type { IDynamicFilterRule } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";
import { Tabs, Tab, FlexRow } from "@similarweb/ui-components";

import categoryService, { ALL } from "app/common/services/categoryService";
import { swSettings } from "app/common/services/swSettings";
import { multiIncludeExcludeType } from "app/components/multi-include-exclude-filter/consts";
import { MultiIncludeExcludeFilterContainer } from "app/components/multi-include-exclude-filter/MultiIncludeExcludeFilterContainer";
import { EAdsTabs } from "app/components/React/PlaResearch/type";
import {
    AD_TYPE,
    INCLUDE_EXCLUDE_TITLE,
    INCLUDE_EXCLUDE_URL,
    SELECTED_CATEGORY,
} from "app/components/widget/widget-filters/components/consts";
import type { IWidgetModelTypesType } from "app/components/widget/widget-types/Widget";
import useActivationQueryWithAccessControl from "app/custom-hooks/activation/useActivationQueryWithAccessControl";
import { translate } from "app/filters/i18nFilter";
import type { ShortActivationSetSchema } from "app/pages/activation/types/api";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { useKeywordAnalysisNavigationTargets } from "app/pages/keyword-research/keyword-analysis/common-components/keywordAnalysisNavigationTargets";
import { SearchAdsTable } from "app/pages/keyword-research/website-analysis/pages/search-ads/search-ads-table/SearchAdsTable";
import {
    SHOPPING,
    TABLE_COLUMN_AD_DETAILS,
    TABLE_COLUMN_AD_TYPE,
    TABLE_COLUMN_AVG_CPC,
    TABLE_COLUMN_CLICKS_CHANGE,
    TABLE_COLUMN_CLICKS_SHARE,
    TABLE_COLUMN_DOMAIN,
    TABLE_COLUMN_KEYWORDS_COUNT,
    TABLE_COLUMN_LANDING_PAGE,
    TABLE_COLUMN_POSITION,
    TABLE_COLUMN_TOP_KEYWORD,
    TABLE_COLUMN_VOLUME,
    TEXT,
    WORLD_WIDE,
} from "app/common/keyword-research/website-search-ads/searchAdsTableConsts";
import type { IDataRecord, IAdData } from "app/common/keyword-research/website-search-ads/searchAdsTableTypes";
import { transformData } from "app/pages/keyword-research/website-analysis/pages/search-ads/search-ads-table/SearchAdsUtils";
import { FiltersContainer } from "app/pages/keyword-research/website-analysis/pages/search-ads/search-ads-table/StyledComponents";
import { useSearchAdsEndpoints } from "app/pages/keyword-research/website-analysis/pages/search-ads/searchAdsEndpoints";
import { adaptMultiIncludeExcludeRules } from "app/pages/keyword-research/website-analysis/pages/search-ads/searchAdsWebsiteUtils";
import { TabListStyled } from "app/components/keyword-research/website-search-ads/styledComponents";
import DurationServiceInstance from "app/services/DurationService";
import { DefaultFetchService, requestParams } from "app/services/fetchService";
import { keywordGroupsTransforms } from "app/services/keyword-groups";
import { createUniqueListOfAllDomains } from "pro-features/components/DomainsFilter/Utils";
import { MultiCategoriesChipDown } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoriesChipDown";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { FiltersSection } from "./FiltersSection";
import {
    getCategoriesOptions,
    onCategorySelected,
} from "app/common/keyword-research/keyword-search-ads/categoryOptionsUtils";

export const ChipdownItem = styled(FlexRow)`
    flex-grow: 0;
`;

export const initialTableSort: ISortProps = { asc: false, sort: TABLE_COLUMN_CLICKS_SHARE };

const getVisibleColumns = (isKeywordGroup: boolean): string[] => {
    const columns = [
        TABLE_COLUMN_AD_TYPE,
        TABLE_COLUMN_AD_DETAILS,
        TABLE_COLUMN_CLICKS_SHARE,
        TABLE_COLUMN_CLICKS_CHANGE,
        TABLE_COLUMN_LANDING_PAGE,
        TABLE_COLUMN_POSITION,
        TABLE_COLUMN_DOMAIN,
    ];

    if (isKeywordGroup) {
        columns.push(TABLE_COLUMN_VOLUME, TABLE_COLUMN_AVG_CPC, TABLE_COLUMN_KEYWORDS_COUNT, TABLE_COLUMN_TOP_KEYWORD);
    }

    return columns;
};

export const SearchAdsKeywordPage: FC = () => {
    const { params } = useCurrentStateAndParams();
    const endpoints = useSearchAdsEndpoints();
    const navTargets = useKeywordAnalysisNavigationTargets();
    const { selectedPageTab } = params;
    const {
        country,
        webSource,
        duration,
        key,
        keyword,
        multiIncludeExcludeUrl,
        multiIncludeExcludeTitle,
        selectedCategory,
        domains,
    } = params;
    const { from, to, isWindow } = DurationServiceInstance.getDurationData(params.duration).forAPI;
    const [selectedPageTabIndex, setSelectedPageTabIndex] = useState(EAdsTabs[selectedPageTab]);
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(params.keyword);
    const categories = categoryService.getCategories().filter((category) => category.id !== ALL);
    const [currentPage, setCurrentPage] = useState(1);
    const [adaptedTitleFilter, setAdaptedTitleFilter] = useState<string | undefined>();
    const [adaptedUrlFilter, setAdaptedUrlFilter] = useState<string | undefined>();

    const {
        data: activationSetsData,
        isLoading: loadingActivationSet,
        isFetching: fetchingActivationSet,
    } = useActivationQueryWithAccessControl();
    const [activationSets, setActivationSets] = useState<ShortActivationSetSchema[]>(undefined);

    useEffect(() => {
        if (!loadingActivationSet && !fetchingActivationSet) {
            setActivationSets(activationSetsData);
        }
    }, [loadingActivationSet, fetchingActivationSet]);

    useEffect(() => {
        const loadFilters = async () => {
            const titleFilter = multiIncludeExcludeTitle
                ? await adaptMultiIncludeExcludeRules(multiIncludeExcludeTitle)
                : undefined;
            const urlFilter = multiIncludeExcludeUrl
                ? await adaptMultiIncludeExcludeRules(multiIncludeExcludeUrl)
                : undefined;

            setAdaptedTitleFilter(titleFilter);
            setAdaptedUrlFilter(urlFilter);
        };

        void loadFilters();
    }, [multiIncludeExcludeTitle, multiIncludeExcludeUrl]);

    const [sortConfig, setSortConfig] = useState<ISortProps>({
        sort: initialTableSort.sort,
        asc: initialTableSort.asc,
    });

    const updateCurrentPage = (page: number) => {
        setCurrentPage(page);
    };

    const updateSort = (sorter: ISortProps) => {
        setSortConfig(sorter);
    };

    const onPageTabSelect = (index: string) => {
        const selectedTabKey = EAdsTabs[index];
        setSelectedPageTabIndex(index);
        proNavigator.updateParams(
            {
                selectedPageTab: selectedTabKey,
            },
            { reload: false },
        );
    };
    const allDomains = createUniqueListOfAllDomains(domains?.split(","), activationSets);

    const apiParams = {
        country: country,
        webSource,
        to,
        from,
        isWindow,
        keys: isKeywordGroup ? keywordGroupsTransforms.getGroupIdFromUrlParam(keyword) : keyword,
        sort: sortConfig.sort,
        asc: sortConfig.asc,
        adType: selectedPageTab === EAdsTabs[EAdsTabs.Text] ? TEXT : SHOPPING,
        selectedCategory,
        page: currentPage,
        DomainsFilter: allDomains,
    };
    const paramsAsString = requestParams(apiParams);

    const getRequestBody = () => {
        try {
            const url: IDynamicFilterRule[] = multiIncludeExcludeUrl ? JSON.parse(multiIncludeExcludeUrl) : [];
            const title: IDynamicFilterRule[] = multiIncludeExcludeTitle ? JSON.parse(multiIncludeExcludeTitle) : [];
            return [
                ...url.map((rule) => ({ ...rule, field: "url" })),
                ...title.map((rule) => ({ ...rule, field: "title" })),
            ];
        } catch (e) {
            Sentry.error("Error making the post request:", e);
            return [];
        }
    };

    const requestBody = getRequestBody();
    const excelUrl = `${endpoints.excel}?${paramsAsString}`;
    const { data, isLoading, isError } = useQuery({
        queryKey: [
            endpoints.table,
            duration,
            key,
            country,
            selectedPageTab,
            sortConfig.asc,
            sortConfig.sort,
            webSource,
            to,
            from,
            isWindow,
            keyword,
            selectedCategory,
            currentPage,
            multiIncludeExcludeUrl,
            multiIncludeExcludeTitle,
            domains,
            allDomains,
        ],
        queryFn: async () =>
            DefaultFetchService.getInstance().post(`${endpoints.table}?${paramsAsString}`, requestBody),
        select: (response: IDataRecord) => transformData(response),
    });

    const addToDashboardConfig = {
        type: "SearchAdsTable" as IWidgetModelTypesType,
        metric: "SearchAdsKeyword",
        modelType: "fromKeyword",
        country,
        duration,
        webSource,
        filters: {
            [AD_TYPE]: selectedPageTab === EAdsTabs[EAdsTabs.Text] ? TEXT : SHOPPING.toLowerCase(),
            [INCLUDE_EXCLUDE_TITLE]: adaptedTitleFilter,
            [INCLUDE_EXCLUDE_URL]: adaptedUrlFilter,
            [SELECTED_CATEGORY]: selectedCategory,
            domains,
        },
    };

    const visibleColumns = getVisibleColumns(isKeywordGroup).filter((column) =>
        country === WORLD_WIDE || country === 999 ? column !== TABLE_COLUMN_POSITION : column,
    );

    return (
        <Tabs
            onSelect={onPageTabSelect}
            selectedIndex={selectedPageTabIndex}
        >
            <TabListStyled size={"lg"}>
                <Tab id={EAdsTabs.Text}>{translate("website.ads.pages.tab.text")}</Tab>
                <Tab id={EAdsTabs.Product}>{translate("website.ads.pages.tab.product")}</Tab>
            </TabListStyled>
            {activationSets ? (
                <>
                    <FiltersContainer>
                        {swSettings.components.Home.resources.HasUnicornsCanary ? (
                            <ChipdownItem>
                                <FiltersSection
                                    domainsAPI={endpoints.domains}
                                    activationSets={activationSets}
                                    sortConfig={sortConfig}
                                    requestBody={requestBody}
                                />
                            </ChipdownItem>
                        ) : null}
                        <ChipdownItem>
                            <MultiIncludeExcludeFilterContainer filterType={multiIncludeExcludeType.TITLE} />
                        </ChipdownItem>
                        <ChipdownItem>
                            <MultiIncludeExcludeFilterContainer filterType={multiIncludeExcludeType.URL} />
                        </ChipdownItem>
                        <ChipdownItem>
                            <MultiCategoriesChipDown
                                categories={getCategoriesOptions(categories)}
                                initialSelectedCategories={selectedCategory}
                                onDone={onCategorySelected}
                                buttonText={translate("website.ads.pages.filters.allCategories")}
                                searchPlaceholder="Search for categories"
                            />
                        </ChipdownItem>
                    </FiltersContainer>
                    <SearchAdsTable<IAdData>
                        type={EAdsTabs[selectedPageTabIndex] === EAdsTabs.Text ? EAdsTabs.Text : EAdsTabs.Product}
                        data={data?.data}
                        isLoading={isLoading}
                        visibleColumns={visibleColumns}
                        isError={isError}
                        sortConfig={sortConfig}
                        handleSort={updateSort}
                        totalCount={data?.totalCount}
                        currentPage={currentPage}
                        onPageChange={updateCurrentPage}
                        addToDashboardConfig={addToDashboardConfig}
                        excelUrl={excelUrl}
                        requestBodyForExcel={requestBody}
                        headerTooltipKey="search.ads.keyword.table.header.tooltip"
                        websiteKeywordsState={navTargets.websiteKeywordsState}
                    />
                </>
            ) : null}
        </Tabs>
    );
};
